import React, { useState } from "react";

import {
  Alert,
  Button,
  Divider,
  Flex,
  Modal,
  Skeleton,
} from "@olist/design-system";
import { DownloadOutline } from "@olist/design-system-icons";

import { useToast } from "components/toast";
import {
  downloadBase64Image,
  imageMimetypes,
  mimetypeToPlaceholder,
} from "pages/ticket/constants";
import { useGetTicketAttachment } from "pages/ticket/hooks/useTicketComment";
import { AttachementImage, ImageNameDownload } from "pages/ticket/styles";
import type {
  IAttachment,
  ITicketDiscussionAttachment,
} from "types/support-tickets";

export function Attachments({
  attachments,
  handleSeeMoreItensClicked,
  isSeeMoreClicked,
}: {
  attachments: ITicketDiscussionAttachment[];
  isSeeMoreClicked: boolean;
  handleSeeMoreItensClicked: () => void;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<IAttachment | undefined>();
  const { getAttachment, isLoading } = useGetTicketAttachment();

  const { handleOpen } = useToast();

  const handleFileOpen = async (file: ITicketDiscussionAttachment) => {
    setIsModalOpen(true);
    await getAttachment(
      { id: file.id },
      {
        onSuccess: (data) => {
          setSelectedImage(data);
        },
        onError: () => {
          handleOpen({
            message: "Tivemos um problema, tente novamente mais tarde",
            variation: "alert",
          });
        },
      }
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedImage(undefined);
  };

  const handleDownloadFile = async () => {
    try {
      await downloadBase64Image(
        selectedImage?.base64,
        selectedImage?.name,
        selectedImage?.mime_type
      );
      console.log("Download feito com sucesso");
    } catch (error) {
      handleOpen({
        message: "Tivemos um problema, tente novamente mais tarde",
        variation: "alert",
      });
    }
  };

  if (attachments === undefined) return null;

  const attachementsList = isSeeMoreClicked
    ? attachments.slice(0, 2)
    : attachments;

  return (
    <>
      <Flex flexDirection="column" gap="stack.medium">
        <Alert
          variation="info"
          description="Serão exibidos apenas arquivos compartilhados após dia 14/10/2025"
        />
        <Flex width="100%" gap="inline.medium" flexWrap="wrap">
          {attachementsList.map((image) => (
            <Flex
              width="100%"
              key={image.id}
              gap="inline.small"
              alignItems="center"
            >
              <AttachementImage
                src={
                  mimetypeToPlaceholder[image.mime_type] ||
                  mimetypeToPlaceholder.default
                }
                alt={`attachment-${image.name}`}
              />
              <ImageNameDownload bold onClick={() => handleFileOpen(image)}>
                {image.name}
              </ImageNameDownload>
            </Flex>
          ))}
        </Flex>
        {attachments.length > 3 && (
          <>
            <Divider />
            <Button variation="secondary" onClick={handleSeeMoreItensClicked}>
              {isSeeMoreClicked ? "ver mais" : "ver menos"}
            </Button>
          </>
        )}
      </Flex>
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Visualizar/Baixar arquivo"
        body={
          <Flex width="100%" alignItems="center" justifyContent="center">
            {isLoading && <Skeleton height="400px" width="600px" />}
            {selectedImage !== undefined &&
              imageMimetypes.includes(selectedImage.mime_type) && (
                <img
                  style={{ objectFit: "contain", width: "600px" }}
                  alt={selectedImage.name}
                  src={`data:${selectedImage.mime_type};base64,${selectedImage.base64}`}
                />
              )}
            {selectedImage !== undefined &&
              !imageMimetypes.includes(selectedImage.mime_type) && (
                <Flex width="100%" gap="inline.small" alignItems="center">
                  <AttachementImage
                    src={
                      mimetypeToPlaceholder[selectedImage.mime_type] ||
                      mimetypeToPlaceholder.default
                    }
                    alt={`attachment-${selectedImage.name}`}
                  />
                  <ImageNameDownload bold>
                    {selectedImage.name}
                  </ImageNameDownload>
                </Flex>
              )}
          </Flex>
        }
        footer={
          <Flex width="100%" justifyContent="space-between">
            <Button variation="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button onClick={handleDownloadFile} iconLeft={DownloadOutline}>
              Baixar Arquivo
            </Button>
          </Flex>
        }
      />
    </>
  );
}
